import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../../hooks/useTheme';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { useAuth } from '../../contexts/AuthContext';
import { usePopupFlow } from '../../hooks/usePopupFlow';

export default function SelectAccount() {
  const { theme, setThemeMode } = useTheme();
  const { containerRef, constraints } = useResponsiveLayout();
  const [themeDropdownOpen, setThemeDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  
  const { isExternal, handleLoginSuccess } = usePopupFlow();

  const accountsList = auth?.getAccountsList ? auth.getAccountsList() : [];

  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const handleSelect = async (index) => {
    if (auth?.switchAccount) {
      await auth.switchAccount(index);
    }
    
    // In External flow, selecting an account generates the OAuth code
    if (isExternal && typeof window !== 'undefined' && window.opener) {
      const selectedAccount = accountsList[index];
      const token = selectedAccount?.access_token || localStorage.getItem('access_token');
      if (token) {
        await handleLoginSuccess(token);
        return;
      }
    }
    
    navigate(`/u/${index}/dashboard/overview`);
  };

  const handleAddAccount = () => {
    if (typeof window !== 'undefined' && window.opener) {
      // If we are already in the popup, update stored prompt so usePopupFlow restores prompt=login
      try {
        const stored = sessionStorage.getItem('oauth_flow_params');
        if (stored) {
          const params = JSON.parse(stored);
          params.prompt = 'login';
          sessionStorage.setItem('oauth_flow_params', JSON.stringify(params));
        }
      } catch (e) {
        console.error('Failed to update oauth_flow_params in sessionStorage:', e);
      }
      navigate('/auth/popup/password');
    } else {
      window.open('/auth/popup', 'AuthPopup', 'width=500,height=650');
    }
  };

  const handleLogoutAll = async () => {
    if (auth?.logoutAll) {
      await auth.logoutAll();
    }
    navigate('/');
  };

  return (
    <>
      {/* Top Navigation Bar - Absolute Root Level (100% Full-Bleed) */}
      <div
        className="bg-white dark:bg-[#0d0e12] border-b border-slate-200 dark:border-[#1f2937]"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '64px',
          zIndex: 50,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingLeft: '24px',
          paddingRight: '24px',
        }}
      >
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 bg-indigo-600 rounded-md flex items-center justify-center text-white font-bold text-sm">OL</div>
          <div className="text-sm font-semibold text-slate-900 dark:text-white">One Login</div>
        </div>

        <div>
          <div className="relative">
            <button
              onClick={() => setThemeDropdownOpen(!themeDropdownOpen)}
              className="p-2 text-lg bg-slate-100 hover:bg-slate-200 dark:bg-white/10 dark:hover:bg-white/20 rounded-lg transition-colors text-slate-900 dark:text-white"
              aria-label="Theme selector"
            >
              {theme === 'light' ? '☀️' : theme === 'dark' ? '🌙' : '🖥️'}
              <span className="sr-only">{theme === 'light' ? 'Light' : theme === 'dark' ? 'Dark' : 'System'}</span>
            </button>

            {themeDropdownOpen && (
              <div className="absolute top-full mt-2 right-0 z-50 w-44 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg shadow-2xl">
                <div className="p-2 border-b border-slate-200 dark:border-slate-600">
                  <p className="text-xs font-semibold text-slate-900 dark:text-white">Theme</p>
                </div>
                <div className="p-1 space-y-1">
                  {[
                    { value: 'light', icon: '☀️', label: 'Light' },
                    { value: 'dark', icon: '🌙', label: 'Dark' },
                    { value: 'system', icon: '🖥️', label: 'System' },
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setThemeMode(option.value);
                        setThemeDropdownOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 rounded-md flex items-center gap-2 text-sm transition-colors ${
                        theme === option.value
                          ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-900 dark:text-indigo-200'
                          : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                      }`}
                    >
                      <span className="text-base">{option.icon}</span>
                      <div className="flex-1">{option.label}</div>
                      {theme === option.value && <span className="text-indigo-600 dark:text-indigo-400">✓</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content - Fluid Flexbox Centering */}
      <div className="min-h-screen w-full bg-slate-50 dark:bg-[#07080a] flex items-center justify-center px-4 pt-16 pb-12 overflow-y-auto">

      {/* Main Container with Responsive Collision Protection */}
      <div ref={containerRef} className={`w-full max-w-md bg-white dark:bg-gray-900/80 backdrop-blur-sm border border-slate-200 dark:border-gray-800 rounded-2xl shadow-2xl transition-all duration-300`}>
        {/* ============= CARD HEADER ============= */}
        <div className={`${constraints.containerPadding} border-b border-slate-200 dark:border-gray-800`}>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Select Account</h1>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Choose which account to access</p>
          </div>
        </div>

         {/* ============= CONTENT AREA ============= */}
         <div className={`${constraints.containerPadding} space-y-4 text-slate-900 dark:text-white`}>
          {/* Accounts List */}
          {accountsList.length === 0 ? (
            <div className="p-4 text-center text-sm text-slate-500">
              No authenticated accounts found.
            </div>
          ) : (
            <ul className="space-y-3">
              {accountsList.map((acct) => {
                const u = acct.user;
                const name = u ? (u.first_name ? `${u.first_name} ${u.last_name || ''}`.trim() : u.username) : `Account ${acct.index + 1}`;
                const email = u?.email || u?.username || `sso_id: ${u?.sso_id || acct.index}`;

                return (
                  <li key={acct.index}>
                    <button
                      type="button"
                      onClick={() => handleSelect(acct.index)}
                      className={`w-full flex items-center gap-4 p-4 rounded-lg border transition-all cursor-pointer text-left shadow-sm hover:shadow-md ${
                        acct.active
                          ? 'bg-indigo-50 dark:bg-indigo-950/40 border-indigo-500 dark:border-indigo-600'
                          : 'bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 border-slate-200 dark:border-slate-700'
                      }`}
                    >
                      {/* Avatar */}
                      <div className="shrink-0">
                        <div className="h-12 w-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-sm font-bold text-white border-2 border-slate-300 dark:border-slate-600">
                          {getInitials(name)}
                        </div>
                      </div>

                      {/* Account Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-slate-900 dark:text-white truncate">{name}</span>
                          {acct.active && (
                            <span className="px-2 py-0.5 text-xs bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 rounded font-medium">Default</span>
                          )}
                        </div>
                        <div className="text-sm text-slate-600 dark:text-slate-400 truncate">{email}</div>
                      </div>

                      {/* Arrow Icon */}
                      <div className="shrink-0 text-slate-400 dark:text-slate-500">
                        →
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Separator */}
          <div className="py-2">
            <div className="h-px bg-slate-200 dark:bg-slate-700" />
          </div>

          {/* Add Another Account & Logout All Buttons */}
          <div className="space-y-2">
            <button
              type="button"
              onClick={handleAddAccount}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 hover:from-indigo-100 hover:to-purple-100 dark:hover:from-indigo-900/40 dark:hover:to-purple-900/40 border border-indigo-200 dark:border-indigo-700 hover:border-indigo-400 dark:hover:border-indigo-600 transition-all text-indigo-700 dark:text-indigo-300 font-semibold"
            >
              <span className="text-xl">+</span>
              <span>Add another account</span>
            </button>

            {accountsList.length > 0 && (
              <button
                type="button"
                onClick={handleLogoutAll}
                className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm font-medium transition-all"
              >
                <span>❌</span>
                <span>Log Out All Accounts</span>
              </button>
            )}
          </div>

          {/* Footer Help Text */}
          <p className="text-xs text-center text-slate-600 dark:text-slate-400 mt-4">
            Securely switch between your multiple authenticated accounts. Each session is isolated and secure.
          </p>
        </div>
      </div>
      </div>
    </>
  );
}



